import React, { createContext, useState, useContext, useEffect } from 'react';
import { firebaseConfig, firestore, auth } from '../../firebase';
import { initializeApp } from "firebase/app";
import { collection, doc, query, where, getDocs, addDoc, deleteDoc, updateDoc, onSnapshot } from "firebase/firestore";
import { onAuthStateChanged } from "firebase/auth";
import { Timestamp } from "firebase/firestore";



export const TaskContext = createContext();

export const useTasks = () => useContext(TaskContext);

export const TaskProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [groups, setGroups] = useState([]);
  const [events, setEvents] = useState([]);
  const [addGroupError, setAddGroupError] = useState("");


  const [loading, setLoading] = useState(true);

  const [error, setError] = useState(null);

  useEffect(() => {
    const unsubscribeAuth = onAuthStateChanged(auth, (authUser) => {
      if (authUser) {
        setUser(authUser);
        setLoading(true);
        // Fetch user-specific groups and events from Firestore
        const unsubscribeGroups = onSnapshot(
          collection(firestore, `users/${authUser.uid}/groups`),
          (snapshot) => {
            const fetchedGroups = snapshot.docs.map(doc => {
              const groupData = { id: doc.id, ...doc.data() };
              // Add groupData only if it does not exist in the state
              if (!groups.some(group => group.id === doc.id)) {
                return groupData;
              }
              return groups.find(group => group.id === doc.id); // Return the existing group from the state
            });
            setGroups(fetchedGroups);
          },
          (error) => {
            setError(error);
            console.error("Error fetching groups:", error);
          }
        );
        

        const unsubscribeEvents = onSnapshot(
          collection(firestore, `users/${authUser.uid}/events`),
          (snapshot) => {
            const fetchedEvents = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
            setEvents(fetchedEvents);
            setLoading(false);
          },
          (error) => {
            setError(error);
            console.error("Error fetching events:", error);
            setLoading(false);
          }
        );

        return () => {
          unsubscribeGroups();
          unsubscribeEvents();
        };
      } else {
        setUser(null);
        setGroups([]);
        setEvents([]);
        setLoading(false);
      }
    }, (error) => {
      setError(error);
      setLoading(false);
    });

    return () => unsubscribeAuth();
  }, []);

  const addGroup = async (name, color) => {
    if (!user) {
      console.log("No user logged in");
      return;
    }
    
    // Check if a group with the same name already exists for the user
    const groupNameLower = name.trim().toLowerCase();
    const existingGroup = groups.some(group => group.name.toLowerCase() === groupNameLower);
    if (existingGroup) {
      setAddGroupError("Group with the same name already exists."); // Set the error message
      return; // Exit the function to prevent adding a duplicate
    }
  
    try {
      const groupsRef = collection(firestore, `users/${user.uid}/groups`);
      const docRef = await addDoc(groupsRef, { name: name.trim(), color });
      // The onSnapshot listener will handle updating the state, so you don't need to do it here
      console.log("Group added with ID:", docRef.id);
    } catch (error) {
      console.error("Error adding group:", error);
      setAddGroupError("Failed to add group."); // Set a general error message
    }
  };


  const addEvent = async (eventData) => {
    if (!user) {
      console.log("No user logged in");
      return;
    }
  
    try {
      const eventToSave = {
        ...eventData,
        start: Timestamp.fromDate(new Date(eventData.start)),
        end: Timestamp.fromDate(new Date(eventData.end)),
      };
  
      console.log("Attempting to add event with data:", eventToSave);
      const eventsRef = collection(firestore, `users/${user.uid}/events`);
      const docRef = await addDoc(eventsRef, eventToSave);
  
      // Check if the event is already added locally before updating the state.
      setEvents(prevEvents => {
        const eventExists = prevEvents.some(event => event.id === docRef.id);
        if (!eventExists) {
          return [...prevEvents, { ...eventToSave, id: docRef.id }];
        } else {
          return prevEvents.map(event =>
            event.id === docRef.id ? { ...eventToSave, id: docRef.id } : event
          );
        }
      });
  
      console.log("Event added with ID:", docRef.id);
    } catch (error) {
      console.log("Error adding event:", error);
    }
  };
  

  
  const editEvent = async (eventId, updates) => {
    console.log("Updates to save:", updates); // This will log what you are actually trying to update
    if (!user || !updates) {
      console.log("No user logged in or updates are undefined");
      return;
    }
  
    try {
      const eventRef = doc(firestore, `users/${user.uid}/events/${eventId}`);
      await updateDoc(eventRef, updates);
      setEvents(events => events.map(event => event.id === eventId ? {...event, ...updates} : event));
      console.log("Event updated:", eventId);
    } catch (error) {
      console.error("Error editing event:", error);
    }
  };
  const deleteEvent = async (eventId) => {
    if (!user) return console.log("No user logged in");
    try {
      const eventRef = doc(firestore, `users/${user.uid}/events/${eventId}`);
      await deleteDoc(eventRef);
      setEvents(events => events.filter(event => event.id !== eventId));
    } catch (error) {
      console.log("Error deleting event:", error);
    }
  };

  const toggleCompletion = async (eventId) => {
    const event = events.find(e => e.id === eventId);
    const newCompletedStatus = !event.completed;
  
    try {
      const eventRef = doc(firestore, `users/${user.uid}/events/${eventId}`);
      await updateDoc(eventRef, { completed: newCompletedStatus });
      // Update local events state with the new completion status
      setEvents(events.map(event => 
        event.id === eventId ? { ...event, completed: newCompletedStatus } : event
      ));
      console.log("Completion toggled for event:", eventId);
    } catch (error) {
      console.error("Error toggling completion:", error);
    }
  };
  

  const deleteGroup = async (groupId) => {
    if (!user) return console.log("No user logged in");
    try {
      const groupRef = doc(firestore, `users/${user.uid}/groups/${groupId}`);
      await deleteDoc(groupRef);
      setGroups(groups => groups.filter(group => group.id !== groupId));
      setEvents(events => events.filter(event => event.groupId !== groupId));
    } catch (error) {
      console.log("Error deleting group:", error);
    }
  };

  return (
    <TaskContext.Provider value={{
      user,
      setUser,
      loading,
      groups,
      events,
      error,
      addGroup,
      addEvent,
      editEvent,
      deleteEvent,
      deleteGroup,
      toggleCompletion,
      addGroupError,
      setAddGroupError
    }}>
      {children}
    </TaskContext.Provider>
  );
};